import { createLogger } from '@/logger';

const log = createLogger('StylesheetInliner');

// Per-stylesheet fetch budget. A slow/unreachable proxy must not stall the
// start of recording — on timeout we move on and a later sweep / the next
// rrweb checkout picks the styles up.
const FETCH_TIMEOUT_MS = 4_000;

export interface StylesheetInlinerDeps {
  /** Backend base URL (same value as RecorderConfig.apiUrl). */
  readonly apiUrl: string;
  readonly apiKey: string;
  /** Injectable for tests. Defaults to the global document / fetch. */
  readonly doc?: Document;
  readonly fetchImpl?: typeof fetch;
}

/**
 * Makes a recording self-contained by inlining cross-origin stylesheets that
 * rrweb cannot read in the browser.
 *
 * Why: a `<link>` to a cross-origin stylesheet has unreadable `cssRules`
 * (CORS), so rrweb's `inlineStylesheet` silently leaves it as an external
 * reference. On replay that styling is missing unless the origin is reachable —
 * which is why e.g. a fixed-position chat widget renders unstyled/off-screen.
 *
 * Fix: fetch the stylesheet text via the backend's allow-listed asset proxy
 * (server-side fetch, no browser CORS) and inject it as a same-origin
 * `<style>` so rrweb captures it inline. The original `<link>` is left in place
 * — we only add styling, never change what the visitor sees.
 */
export class StylesheetInliner {
  private readonly doc: Document;
  private readonly fetchImpl: typeof fetch;
  private observer: MutationObserver | null = null;
  /** Hrefs already fetched (or in-flight) — prevents duplicate work. */
  private readonly handled = new Set<string>();
  /** `<link>`s we've attached a load listener to — avoids duplicate listeners. */
  private readonly watched = new WeakSet<HTMLLinkElement>();
  private stopped = false;

  constructor(private readonly deps: StylesheetInlinerDeps) {
    this.doc = deps.doc ?? document;
    this.fetchImpl = deps.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  /** Inline the stylesheets present now, then watch for ones added later. */
  async start(): Promise<void> {
    await this.sweep();
    this.watchLinkLoads();
    try {
      this.observer = new MutationObserver((records) => {
        const added = records.some((r) =>
          Array.from(r.addedNodes).some(
            (n) =>
              n.nodeName === 'LINK' ||
              (n as Element).querySelector?.('link[rel="stylesheet"]') != null
          )
        );
        if (added) {
          // A newly inserted <link> is rarely loaded yet, so a sweep right now
          // sees it as readable-but-empty and skips it; re-sweep on its `load`.
          this.watchLinkLoads();
          void this.sweep();
        }
      });
      this.observer.observe(this.doc.documentElement, { childList: true, subtree: true });
    } catch {
      // No MutationObserver (very old/embedded engine) — the initial sweep ran.
    }
  }

  /**
   * Re-sweep when a stylesheet finishes loading. A cross-origin sheet usually
   * isn't inline-able at the moment its `<link>` is inserted — it's either not
   * yet in `document.styleSheets` or reads as empty (not "unreadable") until the
   * browser parses it. The MutationObserver fires on insertion, too early, and
   * nothing else re-triggers a sweep once the sheet lands — so an async-loaded
   * third-party widget's CSS would never get inlined. The `<link>` `load` event
   * fires even for cross-origin sheets (only `cssRules` is CORS-blocked), which
   * is exactly the signal we need. Idempotent: each link is wired once.
   */
  private watchLinkLoads(): void {
    const links = this.doc.querySelectorAll?.('link[rel="stylesheet"]');
    if (!links) return;
    for (const node of Array.from(links)) {
      const link = node as HTMLLinkElement;
      if (this.watched.has(link)) continue;
      this.watched.add(link);
      link.addEventListener('load', () => void this.sweep(), { once: true });
    }
  }

  stop(): void {
    this.stopped = true;
    this.observer?.disconnect();
    this.observer = null;
  }

  private async sweep(): Promise<void> {
    if (this.stopped) return;
    await Promise.all(this.unreadableCrossOriginHrefs().map((href) => this.inlineOne(href)));
  }

  /** Hrefs of cross-origin `<link>` stylesheets whose rules can't be read. */
  private unreadableCrossOriginHrefs(): string[] {
    const out: string[] = [];
    for (const sheet of Array.from(this.doc.styleSheets)) {
      const href = sheet.href;
      if (!href || this.handled.has(href)) continue;
      let readable = false;
      try {
        // Touching cssRules throws SecurityError on a cross-origin sheet.
        void sheet.cssRules.length;
        readable = true;
      } catch {
        readable = false;
      }
      // Same-origin (and CORS-readable) sheets rrweb already inlines itself.
      if (!readable) out.push(href);
    }
    return out;
  }

  private async inlineOne(href: string): Promise<void> {
    if (this.handled.has(href)) return;
    this.handled.add(href); // claim eagerly so concurrent sweeps don't double-fetch

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const base = this.deps.apiUrl.replace(/\/$/, '');
      const url = `${base}/api/proxy/css?url=${encodeURIComponent(href)}`;
      const res = await this.fetchImpl(url, {
        headers: { 'x-api-key': this.deps.apiKey },
        signal: controller.signal,
      });
      if (!res.ok) {
        this.handled.delete(href); // let a later sweep retry
        log.warn(`asset proxy returned ${res.status} for ${href}`);
        return;
      }
      const css = await res.text();
      this.injectStyle(href, this.rewriteRelativeUrls(css, href));
    } catch (err) {
      this.handled.delete(href);
      log.warn('inline stylesheet failed', err);
    } finally {
      clearTimeout(timer);
    }
  }

  private injectStyle(href: string, css: string): void {
    if (this.stopped) return;
    const style = this.doc.createElement('style');
    style.setAttribute('data-cobrowse-inlined', href);
    style.textContent = css;

    // Insert in the original <link>'s cascade slot (right after it) rather than
    // at the end of <head>. The inlined rules are the stylesheet's *defaults*;
    // anything applied later — notably runtime brand theming injected by the
    // widget after load — must still override them. Appending at the end would
    // let our defaults win and both break the live page's branding and replay
    // it with the wrong colours. Falls back to appending if the link is gone.
    const link = this.findLinkByHref(href);
    if (link?.parentNode) {
      link.parentNode.insertBefore(style, link.nextSibling);
    } else {
      (this.doc.head ?? this.doc.documentElement).appendChild(style);
    }
  }

  private findLinkByHref(href: string): Element | null {
    const links = this.doc.querySelectorAll?.('link[rel="stylesheet"]');
    if (!links) return null;
    for (const link of Array.from(links)) {
      if ((link as HTMLLinkElement).href === href) return link;
    }
    return null;
  }

  /**
   * Resolve relative `url(...)` references against the stylesheet's own URL.
   * Inlining moves the rules onto the host page, which would otherwise break
   * relative font/image URLs; absolutising them keeps assets resolvable.
   */
  private rewriteRelativeUrls(css: string, baseHref: string): string {
    return css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (match, quote: string, ref: string) => {
      const r = ref.trim();
      if (/^(data:|https?:|\/\/|#)/i.test(r)) return match; // already absolute / data uri
      try {
        return `url(${quote}${new URL(r, baseHref).href}${quote})`;
      } catch {
        return match;
      }
    });
  }
}
